import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Badge, Button } from '@/components/atoms';
import { StoryPicker } from '@/components/StoryPicker/StoryPicker';
import { sitesApi } from '@/services/config';
import { useArticles, useCategories } from '@/hooks/useArticles';
import { useBulkAddManifestations } from '@/hooks/useStories';
import type { ArticleSummary, CategoryInfo } from '@/types/article';
import type { StorySummary } from '@/types/story';

import styles from './Articles.module.css';

type ViewMode = 'grid' | 'list';
const VIEW_STORAGE_KEY = 'wiredesk-articles-view';
const PER_PAGE = 20;

const STATUS_OPTIONS = [
  { value: 'publish,draft', label: 'Todos' },
  { value: 'publish', label: 'Publicados' },
  { value: 'draft', label: 'Rascunhos' },
  { value: 'trash', label: 'Lixeira' },
];
const DEFAULT_STATUS = 'publish,draft';

function loadView(): ViewMode {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    return stored === 'list' || stored === 'grid' ? stored : 'grid';
  } catch {
    return 'grid';
  }
}

export function Articles() {
  const [view, setView] = useState<ViewMode>(loadView);
  const [site, setSite] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [origem, setOrigem] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [after, setAfter] = useState('');
  const [before, setBefore] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bulkFeedback, setBulkFeedback] = useState<string | null>(null);
  const bulkAddMutation = useBulkAddManifestations();

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, view);
    } catch {
      // ignore
    }
  }, [view]);

  const toggleSelected = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const { data: sites } = useQuery({
    queryKey: ['wp-sites', 'active'],
    queryFn: () => sitesApi.list(true),
    staleTime: 5 * 60_000,
  });

  const { data: categories } = useCategories(site || undefined);
  const { data, isLoading, error } = useArticles({
    page,
    per_page: PER_PAGE,
    search: search || undefined,
    status,
    site: site || undefined,
    origem: origem || undefined,
    categories: selectedCategories.length ? selectedCategories : undefined,
    after: after ? `${after}T00:00:00` : undefined,
    before: before ? `${before}T23:59:59` : undefined,
  });

  const changeSite = (slug: string) => {
    setSite(slug);
    setPage(1);
    setSelectedCategories([]);
    clearSelection();
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PER_PAGE)) : 1;
  const categoryMap = useMemo(() => {
    const m = new Map<number, CategoryInfo>();
    categories?.forEach((c) => m.set(c.id, c));
    return m;
  }, [categories]);

  const editorialCategories = useMemo(() => {
    if (!categories) return [];
    return categories
      .filter((c) => c.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [categories]);

  const resetFilters = () => {
    setSearch('');
    setStatus(DEFAULT_STATUS);
    setSelectedCategories([]);
    setAfter('');
    setBefore('');
    setPage(1);
  };

  const toggleCategory = (id: number) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
    setPage(1);
  };

  const hasFilters =
    Boolean(search) ||
    status !== DEFAULT_STATUS ||
    selectedCategories.length > 0 ||
    Boolean(after) ||
    Boolean(before);

  const selectedArticles = (data?.items ?? []).filter((a: ArticleSummary) =>
    selectedIds.has(a.id),
  );

  async function handleLinkBatch(story: StorySummary) {
    setBulkFeedback(null);
    setPickerOpen(false);
    try {
      const items = selectedArticles.map((a) => ({
        kind: 'wp_post' as const,
        external_id: String(a.id),
        external_url: a.link,
        site: a.site,
        label: a.title.length > 60 ? `${a.title.slice(0, 58)}…` : a.title,
      }));
      const result = await bulkAddMutation.mutateAsync({
        storyId: story.id,
        items,
      });
      const parts: string[] = [];
      if (result.added_count) parts.push(`${result.added_count} ligado(s)`);
      if (result.skipped_count)
        parts.push(`${result.skipped_count} já estava(m) ligado(s)`);
      if (result.error_count) parts.push(`${result.error_count} erro(s)`);
      setBulkFeedback(`${story.title}: ${parts.join(' · ')}`);
      clearSelection();
    } catch (e) {
      setBulkFeedback(`Erro: ${String(e)}`);
    }
  }

  return (
    <section>
      <header className={styles.head}>
        <h1 className={styles.title}>Artigos</h1>
        <Badge tone="info" size="sm">
          WordPress
        </Badge>
        {data && (
          <span className={styles.totalBadge}>
            {data.total.toLocaleString('pt-BR')} resultados
          </span>
        )}
        <div className={styles.viewToggle} role="group" aria-label="Modo de visualização">
          <button
            className={`${styles.viewBtn} ${view === 'grid' ? styles.viewBtnActive : ''}`}
            onClick={() => setView('grid')}
            aria-pressed={view === 'grid'}
          >
            Cards
          </button>
          <button
            className={`${styles.viewBtn} ${view === 'list' ? styles.viewBtnActive : ''}`}
            onClick={() => setView('list')}
            aria-pressed={view === 'list'}
          >
            Lista
          </button>
        </div>
      </header>

      <div className={styles.toolbar}>
        <select
          className={styles.siteSelect}
          value={site}
          onChange={(e) => changeSite(e.target.value)}
          aria-label="Filtrar por site WordPress"
          title="WordPress"
        >
          <option value="">Principal (padrão)</option>
          {(sites ?? []).map((s) => (
            <option key={s.slug} value={s.slug}>
              {s.name}
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Buscar título…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className={styles.searchInput}
        />
        <button
          className={`${styles.filterBtn} ${hasFilters ? styles.filterBtnActive : ''}`}
          onClick={() => setShowFilters((s) => !s)}
        >
          Filtros
          {selectedCategories.length > 0 && (
            <Badge tone="info" size="xs">
              {selectedCategories.length}
            </Badge>
          )}
        </button>
        {hasFilters && (
          <button className={styles.clearBtn} onClick={resetFilters}>
            Limpar
          </button>
        )}
      </div>

      {showFilters && (
        <div className={styles.filterPanel}>
          <div className={styles.filterCol}>
            <p className={styles.filterLabel}>Status</p>
            <div className={styles.radioGroup}>
              {STATUS_OPTIONS.map((opt) => (
                <label key={opt.value} className={styles.radio}>
                  <input
                    type="radio"
                    name="status"
                    value={opt.value}
                    checked={status === opt.value}
                    onChange={() => {
                      setStatus(opt.value);
                      setPage(1);
                    }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div className={styles.filterCol}>
            <p className={styles.filterLabel}>Origem</p>
            <div className={styles.radioGroup}>
              {[
                { value: '', label: 'Todos' },
                { value: 'sistema', label: 'Sistema' },
                { value: 'manual', label: 'Manual' },
              ].map((opt) => (
                <label key={opt.value || 'all'} className={styles.radio}>
                  <input
                    type="radio"
                    name="origem"
                    value={opt.value}
                    checked={origem === opt.value}
                    onChange={() => {
                      setOrigem(opt.value);
                      setPage(1);
                    }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div className={styles.filterCol}>
            <p className={styles.filterLabel}>Período</p>
            <div className={styles.dateRow}>
              <input
                type="date"
                value={after}
                onChange={(e) => {
                  setAfter(e.target.value);
                  setPage(1);
                }}
                className={styles.dateInput}
              />
              <span className={styles.dateSep}>→</span>
              <input
                type="date"
                value={before}
                onChange={(e) => {
                  setBefore(e.target.value);
                  setPage(1);
                }}
                className={styles.dateInput}
              />
            </div>
          </div>

          <div className={styles.filterColWide}>
            <p className={styles.filterLabel}>Editoria / Categoria</p>
            <div className={styles.catGrid}>
              {editorialCategories.length === 0 && (
                <span className={styles.muted}>Sem categorias</span>
              )}
              {editorialCategories.map((cat) => (
                <label key={cat.id} className={styles.catChip}>
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(cat.id)}
                    onChange={() => toggleCategory(cat.id)}
                  />
                  <span>{cat.name}</span>
                  <span className={styles.catCount}>{cat.count}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className={styles.selectionBar}>
          <span className={styles.selectionCount}>
            {selectedIds.size} artigo{selectedIds.size === 1 ? '' : 's'} seleccionado
            {selectedIds.size === 1 ? '' : 's'}
          </span>
          <div className={styles.selectionActions}>
            <Button
              size="sm"
              onClick={() => setPickerOpen(true)}
              loading={bulkAddMutation.isPending}
            >
              Ligar a story
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSelection}>
              Limpar selecção
            </Button>
          </div>
        </div>
      )}

      {bulkFeedback && (
        <div className={styles.bulkFeedback}>{bulkFeedback}</div>
      )}

      {isLoading && <p className={styles.muted}>A carregar…</p>}
      {error && <p className={styles.error}>Falha: {String(error)}</p>}

      {data && data.items.length === 0 && (
        <div className={styles.empty}>Sem artigos com estes filtros.</div>
      )}

      {data && data.items.length > 0 && view === 'grid' && (
        <ul className={styles.gridList}>
          {data.items.map((article) => {
            const catNames = article.categories
              .map((cid) => categoryMap.get(cid)?.name)
              .filter(Boolean) as string[];
            const checked = selectedIds.has(article.id);
            return (
              <li
                key={article.id}
                className={`${styles.gridCard} ${checked ? styles.gridCardSelected : ''}`}
              >
                <label className={styles.gridSelect}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSelected(article.id)}
                    aria-label="Seleccionar artigo"
                  />
                </label>
                {article.featured_media ? (
                  <img
                    src={article.featured_media.source_url}
                    alt={article.featured_media.alt_text ?? ''}
                    className={styles.gridThumb}
                    loading="lazy"
                  />
                ) : (
                  <div className={styles.gridThumbPlaceholder}>sem imagem</div>
                )}
                <div className={styles.gridBody}>
                  <Link
                    to={`/artigos/${article.id}${site ? `?site=${site}` : ''}`}
                    className={styles.gridLink}
                  >
                    {article.title}
                  </Link>
                  {article.excerpt && (
                    <p className={styles.gridExcerpt}>{article.excerpt}</p>
                  )}
                  <div className={styles.gridMeta}>
                    <span>{new Date(article.published_at).toLocaleString('pt-BR')}</span>
                    <StatusBadge status={article.status} />
                    <OrigemBadge origem={article.origem} />
                    {catNames.slice(0, 2).map((n) => (
                      <Badge key={n} tone="neutral" size="xs">
                        {n}
                      </Badge>
                    ))}
                    {article.twitter_post_id && (
                      <Badge tone="info" size="xs">
                        tweet
                      </Badge>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {data && data.items.length > 0 && view === 'list' && (
        <ul className={styles.list}>
          {data.items.map((article) => {
            const catNames = article.categories
              .map((cid) => categoryMap.get(cid)?.name)
              .filter(Boolean) as string[];
            const checked = selectedIds.has(article.id);
            return (
              <li
                key={article.id}
                className={`${styles.row} ${checked ? styles.rowSelected : ''}`}
              >
                <label className={styles.rowSelect}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSelected(article.id)}
                    aria-label="Seleccionar artigo"
                  />
                </label>
                {article.featured_media && (
                  <img
                    src={article.featured_media.source_url}
                    alt={article.featured_media.alt_text ?? ''}
                    className={styles.thumb}
                    loading="lazy"
                  />
                )}
                <div className={styles.body}>
                  <Link
                    to={`/artigos/${article.id}${site ? `?site=${site}` : ''}`}
                    className={styles.rowLink}
                  >
                    {article.title}
                  </Link>
                  <div className={styles.meta}>
                    <span>{article.site}</span>
                    <span>·</span>
                    <span>{new Date(article.published_at).toLocaleString('pt-BR')}</span>
                    <StatusBadge status={article.status} />
                    <OrigemBadge origem={article.origem} />
                    {catNames.map((n) => (
                      <Badge key={n} tone="neutral" size="xs">
                        {n}
                      </Badge>
                    ))}
                    {article.twitter_post_id && (
                      <Badge tone="info" size="xs">
                        tweet
                      </Badge>
                    )}
                  </div>
                  {article.excerpt && <p className={styles.excerpt}>{article.excerpt}</p>}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {data && totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={styles.pageBtn}
          >
            ← Anterior
          </button>
          <span className={styles.muted}>
            Página {page} de {totalPages.toLocaleString('pt-BR')}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className={styles.pageBtn}
          >
            Próxima →
          </button>
        </div>
      )}

      <StoryPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={handleLinkBatch}
      />
    </section>
  );
}

function OrigemBadge({ origem }: { origem?: string }) {
  if (!origem) return null;
  // rótulos legados sistema/manual ainda aparecem; os ricos (ex.: 'Inteligência · auto') também
  const label = origem === 'sistema' ? 'Sistema' : origem === 'manual' ? 'Manual' : origem;
  const lo = origem.toLowerCase();
  const tone = lo.includes('manual') ? 'warn' : lo === 'sistema' || lo.includes('·') ? 'info' : 'neutral';
  return <Badge tone={tone} size="xs">{label}</Badge>;
}

function StatusBadge({ status }: { status?: string }) {
  // rascunho ganha tag; publicado aparece normal (sem badge), como as outras
  if (status === 'draft') return <Badge tone="warn" size="xs">rascunho</Badge>;
  if (status === 'trash') return <Badge tone="neutral" size="xs">lixeira</Badge>;
  return null;
}
